// Phase 5 T17 — recipient picker.
//
// Behaviors:
//   - typeahead vs. /exhibitor/event_settings/recipient_search (debounced)
//   - external-email synthetic option when the query matches an email regex
//     but no linked user matches
//   - keyboard navigation (ArrowUp/ArrowDown/Enter/Escape) + WCAG-required
//     aria-activedescendant on the input
//   - POST add_recipient -> server replies with a Turbo Stream replacing
//     #recipient-list-<email_type>; we render it via Turbo.renderStreamMessage
//   - outside-click dismiss bound via data-action="click@window->...#hideOnOutside"
//   - AbortController cancels any in-flight search on each keystroke and on
//     disconnect (prevents stale results overwriting newer ones during fast
//     typing or partial Turbo re-renders)
//
// [v1.1 FIX #13] Turbo MUST be imported explicitly — it is not on window in
// this project (verified app/frontend/packs/application.js + Phase 4 builder
// controller note). Without the import, renderStreamMessage throws.
import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["input", "dropdown", "chips"]
  static values = {
    searchUrl: String,
    createUrl: String,
    eventExhibitorId: Number,
    emailType: String,
    debounceMs: { type: Number, default: 250 }
  }

  connect() {
    this.abortController = null
    this.timer = null

    const dropdownId = this.dropdownTarget.id || ""
    this.inputTarget.setAttribute("role", "combobox")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.setAttribute("aria-controls", dropdownId)
    this.inputTarget.setAttribute("aria-autocomplete", "list")
    this.inputTarget.setAttribute("autocomplete", "off")

    this._keydownHandler = (e) => this.handleKeydown(e)
    this.inputTarget.addEventListener("keydown", this._keydownHandler)
  }

  disconnect() {
    // Cancel in-flight fetches so a slow recipient_search response can't
    // resurrect a stale dropdown after a partial Turbo re-render.
    this.abortController?.abort()
    if (this.timer) clearTimeout(this.timer)
    this.inputTarget.removeEventListener("keydown", this._keydownHandler)
  }

  search() {
    const q = this.inputTarget.value.trim()
    if (q.length < 2) { this.hideDropdown(); return }
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.performSearch(q), this.debounceMsValue)
  }

  async performSearch(q) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(q)}`
      const res = await fetch(url, {
        headers: { "Accept": "application/json" },
        signal: this.abortController.signal
      })
      if (!res.ok) return
      const results = await res.json()
      this.renderDropdown(results, q)
    } catch (e) {
      if (e.name !== "AbortError") console.error(e)
    }
  }

  renderDropdown(results, query) {
    while (this.dropdownTarget.firstChild) {
      this.dropdownTarget.removeChild(this.dropdownTarget.firstChild)
    }

    // LRPO-5191 — the ticket requires an already-added recipient to disappear
    // from (not just be rejected by) the picker. We read identity from the live
    // chips list every render so the exclusion reflects the latest state — the
    // list is replaced via Turbo Stream after each add, so a just-added
    // recipient is excluded on the very next search. Exclude (rather than render
    // a disabled "Already added" row) to keep the dropdown to selectable
    // options only, matching this controller's keyboard-nav model where every
    // [role=option] is actionable.
    const added = this.currentRecipients()

    let optionIndex = 0

    results
      .filter((r) => !added.userIds.has(String(r.id)))
      .forEach((r) => {
        const li = this._buildOption(`rp-opt-${this.emailTypeValue}-${optionIndex++}`,
                                     r.name ? `${r.name} (${r.email})` : r.email)
        li.addEventListener("click", () => this.addUser(r))
        this.dropdownTarget.appendChild(li)
      })

    if (this.dropdownTarget.children.length === 0 && this.looksLikeEmail(query)) {
      const normalizedQuery = this.normalizeEmail(query)
      if (!added.emails.has(normalizedQuery)) {
        const li = this._buildOption(`rp-ext-${this.emailTypeValue}`,
                                     `Add external email: ${query}`)
        li.addEventListener("click", () => this.addExternalEmail(query))
        this.dropdownTarget.appendChild(li)
      }
    }

    // LRPO-5287 — when a real query (≥2 chars, guaranteed by search()) yields no
    // selectable options — every user hit was already added AND the query is not
    // an addable external email — show a non-actionable empty-state row instead
    // of silently hiding the dropdown, so the user gets explicit "no results"
    // feedback. This row is DELIBERATELY NOT role=option: no id, no click
    // handler, no data-index, and without list-group-item-action it isn't
    // styled as clickable. handleKeydown() and the test harness both collect
    // selectable items via [role=option], so keyboard nav,
    // aria-activedescendant, and Enter/click selection are all unaffected.
    if (this.dropdownTarget.children.length === 0) {
      const empty = document.createElement("li")
      empty.className = "list-group-item disabled text-muted small"
      empty.textContent = "No matching results"
      this.dropdownTarget.appendChild(empty)
    }

    // The dropdown now always has content for a real query (options or the
    // empty-state row), so it stays visible; aria-expanded tracks that content.
    const hasItems = this.dropdownTarget.children.length > 0
    this.dropdownTarget.classList.toggle("d-none", !hasItems)
    this.inputTarget.setAttribute("aria-expanded", hasItems ? "true" : "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  // LRPO-5191 — identity of every recipient currently shown for THIS picker,
  // read from the chips list rendered by _recipient_list.html.erb. A linked
  // user carries data-recipient-user-id; an external address carries
  // data-recipient-email (stored normalized). Returns { userIds:Set<string>,
  // emails:Set<string> } where emails are normalized for case/whitespace.
  currentRecipients() {
    const userIds = new Set()
    const emails = new Set()
    const list = document.getElementById(`recipient-list-${this.emailTypeValue}`)
    if (!list) return { userIds, emails }

    list.querySelectorAll("[data-recipient-email]").forEach((li) => {
      const uid = li.getAttribute("data-recipient-user-id")
      if (uid) userIds.add(String(uid))
      const email = this.normalizeEmail(li.getAttribute("data-recipient-email"))
      if (email) emails.add(email)
    })
    return { userIds, emails }
  }

  normalizeEmail(value) {
    return (value || "").trim().toLowerCase()
  }

  _buildOption(id, label) {
    const li = document.createElement("li")
    li.className = "list-group-item list-group-item-action"
    li.setAttribute("role", "option")
    li.id = id
    li.textContent = label
    return li
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("d-none")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  hideOnOutside(event) {
    if (!this.element.contains(event.target)) this.hideDropdown()
  }

  handleKeydown(e) {
    const items = Array.from(this.dropdownTarget.querySelectorAll("[role=option]"))
    if (items.length === 0) return
    const active = this.dropdownTarget.querySelector(".active")
    const idx = items.indexOf(active)
    let next = null

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        next = items[idx + 1] || items[0]
        break
      case "ArrowUp":
        e.preventDefault()
        next = items[idx - 1] || items[items.length - 1]
        break
      case "Enter":
        if (active) { e.preventDefault(); active.click() }
        return
      case "Escape":
        this.hideDropdown()
        return
      default:
        return
    }

    if (active) active.classList.remove("active")
    if (next) {
      next.classList.add("active")
      this.inputTarget.setAttribute("aria-activedescendant", next.id)
    }
  }

  looksLikeEmail(s) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)
  }

  async addUser(user) {
    await this.postRecipient({ user_id: user.id })
  }

  async addExternalEmail(email) {
    await this.postRecipient({ external_email: email })
  }

  async postRecipient(payload) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const body = { email_type: this.emailTypeValue, ...payload }

    const res = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrf || ""
      },
      body: JSON.stringify(body)
    })

    if (res.ok) {
      const stream = await res.text()
      Turbo.renderStreamMessage(stream)
      this.inputTarget.value = ""
      this.hideDropdown()
    } else {
      // Surface server-side validation in the dev console; product-level
      // error UX (toast / inline error) is tracked under T29's onboarding
      // banner stack and intentionally out of scope here.
      console.error("recipient-picker: addRecipient failed", res.status)
    }
  }
}
